import { useTranslation } from "@coworker/locales";
import { useMemo } from "react";
import {
  FilterType,
  InputType,
  SelectFilter,
} from "../../shared/filters/types/filter";
import { useCustomAllSelectOption } from "../../shared/filters/utils/useAllSelectOption";
import {
  GROUP_LOCATIONS_ID,
  LOCATIONS_ID,
  TASK_TYPE_ID,
} from "../constants/filterIds";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

const UPCOMING_TASK_TYPES = [
  "addonOrder",
  "custom",
  "mfaq",
  "PQR",
  "productIssue",
  "roomSettings",
  "testbuy",
  "testBuyFollowUp",
];

type TaskTypeFilterProps = {
  isUpcoming?: boolean;
};

export function useTaskTypeFilter({
  isUpcoming = false,
}: TaskTypeFilterProps = {}): SelectFilter {
  const { t } = useTranslation();
  const enableAddonOrdersV2 = useFlag(FLAGS.ENABLE_ADDON_ORDERS_V2);
  const defaultValue = { filterId: TASK_TYPE_ID, value: "" };

  const allOptions = useMemo(() => {
    const options = [];

    // Add v2 addon option when flag is enabled, otherwise v1 options
    if (enableAddonOrdersV2) {
      options.push({
        value: "addonOrder",
        title: t("addonOrderString"),
        dependentOfFilterId: GROUP_LOCATIONS_ID,
      });
    } else {
      options.push(
        {
          value: "pickAndRefill",
          title: t("addonTitleString"),
          dependentOfFilterId: GROUP_LOCATIONS_ID,
        },
        {
          value: "refill",
          title: t("refillString"),
          dependentOfFilterId: GROUP_LOCATIONS_ID,
        }
      );
    }

    // Add remaining task type options
    options.push(
      {
        value: "productIssue",
        title: t("taskTypeTitleProductIssue"),
        dependentOfFilterId: LOCATIONS_ID,
      },
      {
        value: "PQR",
        title: t("capitalCaseProductQualityReportString"),
        dependentOfFilterId: LOCATIONS_ID,
      },
      {
        value: "roomSettings",
        title: t("mediaMaintenanceString"),
      }
    );

    options.push(
      {
        value: "testbuy",
        title: t("testBuyString"),
        dependentOfFilterId: LOCATIONS_ID,
      },
      {
        value: "testBuyFollowUp",
        title: t("testBuyFollowUpString"),
        dependentOfFilterId: LOCATIONS_ID,
      }
    );

    options.push(
      {
        value: "mfaq",
        title: t("mfaqFollowUpTitle"),
        dependentOfFilterId: LOCATIONS_ID,
      },
      {
        value: "custom",
        title: t("TasksString"),
        dependentOfFilterId: LOCATIONS_ID,
      }
    );

    return options;
  }, [t, enableAddonOrdersV2]);

  const options = useMemo(
    () =>
      isUpcoming
        ? allOptions.filter((option) =>
            UPCOMING_TASK_TYPES.includes(option.value)
          )
        : allOptions,
    [isUpcoming, allOptions]
  );

  return useCustomAllSelectOption(
    {
      inputType: InputType.Select,
      filterType: FilterType.Filter,
      id: TASK_TYPE_ID,
      title: t("task_typeString"),
      options,
      enabled: true,
      defaultValue,
    },
    t("allTaskTypesString")
  );
}
